import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { useAuth } from '../../auth/useAuth';
import { AuthStatus } from '../AuthStatus';
import { fetchReportLibrary, type ReportLibraryItem } from '../../lib/apiClient';
import { routes } from '../../lib/assessment/routes';
import { assessmentLabel, formatCompletedAt } from '../../lib/report/reportView';

type PageState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ok'; reports: ReportLibraryItem[] };

export type ReportLibraryProps = {
  /**
   * Heading level for this instance (U1b).
   *
   * `h1` is the page at `/reports`. The dashboard embeds the same component as
   * one section among several, where a second `h1` would be wrong for both the
   * document outline and assistive navigation — so it passes `h2`. Embedding
   * rather than forking is deliberate: a copy would drift from the empty state,
   * the error copy, and the link shape this already gets right.
   */
  headingLevel?: 'h1' | 'h2';
};

/** Durable return access: the caller's unlocked reports. */
export function ReportLibrary({ headingLevel = 'h1' }: ReportLibraryProps = {}) {
  const Heading = headingLevel;
  const { status, session } = useAuth();
  // Derived from `status` so the fetch gate matches the render gate below; a
  // password-recovery session carries a valid token but is not signed in.
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;
  const [state, setState] = useState<PageState>({ phase: 'loading' });

  useEffect(() => {
    if (status === 'loading' || !token) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await fetchReportLibrary(token);
      if (cancelled) return;
      if (res.state === 'ok') setState({ phase: 'ok', reports: res.reports });
      else setState({ phase: 'error' });
    })();
    return () => {
      cancelled = true;
    };
  }, [token, status]);

  if (status !== 'signed-in') {
    return (
      <section className="flex flex-col gap-3">
        <p className="text-foreground">Sign in to see your reports.</p>
        <AuthStatus />
      </section>
    );
  }
  if (state.phase === 'loading')
    return <p className="text-muted-foreground">Loading your reports…</p>;
  if (state.phase === 'error')
    return <p className="text-destructive">Your reports could not be loaded.</p>;

  return (
    <section className="flex flex-col gap-4" data-testid="report-library">
      <Heading className="text-2xl font-semibold">Your reports</Heading>
      {state.reports.length === 0 ? (
        <p className="text-foreground">You have not unlocked any full reports yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {state.reports.map((r) => (
            <li key={r.resultId}>
              <Link
                href={routes.report(r.resultId)}
                className="block rounded-lg border border-border p-4 transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <div className="font-medium">{r.primaryType}</div>
                <div className="text-sm text-muted-foreground">
                  {assessmentLabel(r.assessmentType)}
                  {r.completedAt ? ` · ${formatCompletedAt(r.completedAt)}` : ''}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
